/**
 * Images Modal Controller
 *
 * Manages the image gallery modal for viewing lot images
 * @module portal/modules/ui/modals/imagesModal
 */

import { Lot } from '../../types';
import { escapeAttr } from '../../formatting';
import { collectLotImages, __isArchived } from '../../image';
import { els } from '../elements';
import { captureFocus, restoreFocus } from './focusManager';
import { openImageViewer } from './imageViewerModal';

const bodyEl = document.body;

// Store image data for the currently open modal
let currentImages: Array<{ src: string; alt: string }> = [];

/**
 * Check if the images modal is currently open
 */
export function isImagesModalOpen(): boolean {
  return !!(els.imagesModal && !els.imagesModal.hidden);
}

/**
 * Open the images modal with lot images
 * @param lot - The lot whose images to display
 */
export function openImagesModal(lot: Lot): void {
  if (!lot) return;

  captureFocus('lastActiveBeforeImagesModal');

  const allImages = collectLotImages(lot);
  // Filter out archived images
  const sanitizedImages = allImages.filter(entry => {
    const canonical = entry && (entry.canonicalUrl || entry.displayUrl);
    const display = entry && (entry.displayUrl || entry.canonicalUrl);
    return !__isArchived(canonical || display);
  });

  if (els.imagesModalTitle) {
    const lotTitle = lot.title || 'Lot';
    els.imagesModalTitle.textContent = `Images for ${lotTitle}`;
  }

  if (els.imagesModalContent) {
    if (sanitizedImages.length === 0) {
      els.imagesModalContent.innerHTML = '<p class="muted-text">No images available.</p>';
      currentImages = [];
    } else {
      // Store image data for fullscreen viewer
      currentImages = [];
      let html = '<ul class="image-list">';
      for (let i = 0; i < sanitizedImages.length; i++) {
        const entry = sanitizedImages[i];
        if (!entry) {
          continue;
        }
        const canonical = entry.canonicalUrl || entry.displayUrl;
        const displaySrc = entry.displayUrl || entry.canonicalUrl || '';
        const altText = `${lot.title || 'Lot '} image ${i + 1}`;
        const ariaLabel = `View image ${i + 1}`;

        // Store image data
        currentImages.push({
          src: displaySrc,
          alt: altText,
        });

        html += '<li>';
        html += `<button type="button" class="image-tile" aria-label="${escapeAttr(ariaLabel)}" data-image-index="${i}">`;
        const lotIdAttr = lot.id ? ` data-lot-id="${escapeAttr(lot.id)}"` : '';
        html += `<img src="${escapeAttr(displaySrc)}" alt="${escapeAttr(
          altText
        )}" loading="lazy" data-image-url="${escapeAttr(canonical || displaySrc)}"${lotIdAttr} />`;
        html += '</button>';
        html += '</li>';
      }
      html += '</ul>';
      els.imagesModalContent.innerHTML = html;

      // Set up click handlers for image tiles
      setupImageTileHandlers();
    }
  }

  if (els.imagesModalBackdrop) {
    els.imagesModalBackdrop.hidden = false;
  }
  if (els.imagesModal) {
    els.imagesModal.hidden = false;
  }
  if (bodyEl) {
    bodyEl.classList.add('modal-open');
  }

  window.requestAnimationFrame(() => {
    if (els.closeImagesModal) {
      (els.closeImagesModal as HTMLElement).focus();
    }
  });
}

/**
 * Set up click handlers for image tiles to open fullscreen viewer
 */
function setupImageTileHandlers(): void {
  const imageTiles = document.querySelectorAll('.image-list .image-tile');
  imageTiles.forEach(tile => {
    tile.addEventListener('click', e => {
      const button = e.currentTarget as HTMLButtonElement;
      const indexStr = button.getAttribute('data-image-index');
      const index = indexStr ? parseInt(indexStr, 10) : 0;
      if (currentImages.length > 0) {
        openImageViewer(currentImages, index);
      }
    });
  });
}

/**
 * Close the images modal
 */
export function closeImagesModal(): void {
  if (els.imagesModal) {
    els.imagesModal.hidden = true;
  }
  if (els.imagesModalBackdrop) {
    els.imagesModalBackdrop.hidden = true;
  }
  if (bodyEl) {
    bodyEl.classList.remove('modal-open');
  }

  restoreFocus('lastActiveBeforeImagesModal');
}
